import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {
    registerTemplateGraphNode,
    controlPointCommon,
    curveParametersCommon,
    MeshCurve,
    sceneInteractiveMarker,
    getTemplateNodeReturns,
    getControlPointPartNumber,
    isControlPointPart,
    EMPTY_LIST,
    TypeDescriptors,
    visitNone,
} from "@cm/template-nodes"
import type {
    CurveParametersCommon,
    UVControlPoint as MeshCurveUVControlPoint,
    MeshLikeNode,
    SceneInteractiveNode,
    TemplateNode,
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    IGetInfoNodeParts,
    INodeSceneInteractionsContext,
    TemplateNodePart,
    TransformInteraction,
    BuilderInlet,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {mattressCurveLikeMarker} from "#mattress/role-markers"
import {mattressGlobalReferenceFrame, mattressReferenceFrameLike} from "#mattress/validation"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import type {MattressReferenceFrameData} from "#mattress/nodes/reference-frame/mattress-reference-frame"
import type {MattressCurveLocalNodesData, MattressNodePath} from "#mattress/nodes/component/mattress-component-types"
import {getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"

import {Matrix4, Vector2, Vector3} from "@cm/math"
import type {Vector2Like} from "@cm/math"

const TD = TypeDescriptors

const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

export const MattresCurveUVChannel = 1

export type MattressGlobalReferenceFrameKind = "global"

export type MattressUVControlPoint = {
    uv: [number, number]
    corner: boolean
    sectionBreak?: boolean
    uvSpace: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrameKind
}

const mattressUVControlPoint = z.extend(
    z.object({
        uv: z.tuple([z.number(), z.number()]),
        uvSpace: z.union([mattressReferenceFrameLike, mattressGlobalReferenceFrame]),
    }),
    controlPointCommon.shape,
)

export type MattressCurveControlPointData = {
    uv: [number, number]
    corner: boolean
    sectionBreak?: boolean
    uvSpace: MattressReferenceFrameData | "global"
}

export interface MattressCurveLocalControlPointsHelpers {
    toGlobalUVSpace(uv: Vector2Like, uvSpace: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrameKind, context: IGetInfoNodeParts): Vector2Like
    fromGlobalUVSpace(uv: Vector2Like, uvSpace: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrameKind, context: IGetInfoNodeParts): Vector2Like
    getLocalMeshCurveControlPoints(context: IGetInfoNodeParts): MeshCurveUVControlPoint[]
}

export type MattressCurveParameters = {
    name: string
    controlPoints: MattressUVControlPoint[]
} & CurveParametersCommon

const definition: TemplateNodeDefinition<MattressCurveParameters> = {
    nodeClass: "MattressCurve",
    validation: {
        parameters: z.extend(
            z.object({
                name: z.string(),
                controlPoints: z.array(mattressUVControlPoint),
            }),
            curveParametersCommon.shape,
        ),
        returns: getTemplateNodeReturns(),
    },
}

export function getMattressCurveLocalNode(
    scope: GraphBuilderScope,
    curveLocalNodeSrc: BuilderInlet<MeshCurve>,
    resolution: BuilderInlet<number | undefined>,
    closed: BuilderInlet<boolean>,
    controlPointsData: BuilderInlet<MattressCurveControlPointData[]>,
    baseMeshLocalNode: BuilderInlet<MeshLikeNode | null>,
): BuilderOutlet<MeshCurve> {
    return scope.branch(
        scope.pureLambda(
            scope.tuple(curveLocalNodeSrc, resolution, closed, controlPointsData, baseMeshLocalNode),
            ([localMeshCurve, resolution, closed, controlPointsData, baseMeshLocalNode]) => {
                const localNodeControlPoints = controlPointsData.map((data) => {
                    let globalUV: [number, number]
                    if (data.uvSpace === "global") globalUV = data.uv
                    else {
                        const transformed = data.uvSpace.toGlobalSpace({x: data.uv[0], y: data.uv[1]})
                        globalUV = [transformed.x, transformed.y]
                    }
                    return {
                        corner: data.corner,
                        sectionBreak: data.sectionBreak ?? false,
                        uv: globalUV,
                    }
                })

                return localMeshCurve.clone({
                    cloneInstanceIds: true,
                    cloneSubNode: () => false,
                    parameterOverrides: {
                        resolution,
                        closed,
                        controlPoints: localNodeControlPoints,
                        mesh: baseMeshLocalNode,
                    },
                })
            },
            "localMeshCurve",
        ),
    )[0]
}

@registerTemplateGraphNode
export class MattressCurve
    extends MattressNode<MattressCurveParameters>
    implements MattressCurveLikeNode, MattressCurveLocalControlPointsHelpers, SceneInteractiveNode
{
    get [mattressCurveLikeMarker]() {
        return true as const
    }
    get [sceneInteractiveMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressCurveParameters> {
        const {closed, resolution, controlPoints} = this.parameters
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {templateScope} = evaluator
        const {evaluatedData, nodePath, evaluatePartialMattressMeshData} = mattressContext
        const {localNodesDataLists} = evaluatedData

        const scope = evaluator.getScope(this)

        const [mattressMeshDataValid] = scope.branch(evaluatePartialMattressMeshData(scope, evaluator))
        const [baseMeshLocalNodesValid] = scope.branch(scope.get(mattressMeshDataValid, "baseMeshLocalNodes"))
        const baseMeshLocalNode = scope.get(baseMeshLocalNodesValid, "mesh")

        const getControlPointData = (controlPoint: MattressUVControlPoint, controlPointIdx: number): BuilderOutlet<MattressCurveControlPointData | null> => {
            const uvSpaceData = controlPoint.uvSpace === "global" ? ("global" as const) : evaluator.evaluate(controlPoint.uvSpace)
            return scope.pureLambda(
                scope.tuple(uvSpaceData, controlPoint.uv, controlPoint.corner, controlPoint.sectionBreak),
                ([uvSpace, uv, corner, sectionBreak]) => {
                    if (uvSpace === null) return null
                    return {
                        uv,
                        corner,
                        sectionBreak: sectionBreak ?? false,
                        uvSpace,
                    }
                },
                `controlPointData_${controlPointIdx}`,
            )
        }

        const [controlPointsData] = scope.branch(
            scope.pureLambda(
                scope.list(controlPoints.map((cp, idx) => getControlPointData(cp, idx))),
                (controlPointsData) => {
                    const validControlPointsData = controlPointsData.filter((data): data is MattressCurveControlPointData => data !== null)
                    if (validControlPointsData.length !== controlPointsData.length) return null
                    return validControlPointsData
                },
                "controlPointsData",
            ),
        )

        const localMeshCurveSrc = scope.const(
            () =>
                new MeshCurve({
                    parameters: {
                        name: this.parameters.name,
                        resolution: undefined,
                        closed: false,
                        controlPoints: EMPTY_LIST as MeshCurveUVControlPoint[],
                        mesh: null,
                        uvChannel: MattresCurveUVChannel,
                        visible: true,
                    },
                }),
            "localMeshCurveSrc",
        )

        const curveLocalNode = getMattressCurveLocalNode(scope, localMeshCurveSrc, resolution, closed, controlPointsData, baseMeshLocalNode)

        const newNodePath = scope.pureLambda(scope.tuple(nodePath, this), ([nodePath, instance]) => [...nodePath, instance], "newPath", {
            output: mattressNodePathArrayDesc,
        })
        const localNodesData = scope.struct<MattressCurveLocalNodesData>("localNodesData", {
            type: "mattressCurve",
            nodes: scope.list([curveLocalNode]),
            path: newNodePath,
        })
        const localNodesDataList = scope.phi(
            scope.pureLambda(localNodesData, (localNodesData) => (localNodesData ? [localNodesData] : EMPTY_LIST), "localNodesDataList"),
            EMPTY_LIST,
        )
        localNodesDataLists.push(localNodesDataList)

        const curveData = scope.struct<MattressCurveData>("curveData", {
            localNode: curveLocalNode,
            controlPoints: controlPointsData,
            path: newNodePath,
            closed,
            resolution,
        })

        const localId = evaluator.getLocalId(this)
        templateScope.alias(curveData, `mattressCurveData-${localId}`)

        return visitNone(this.parameters)
    }

    canTransformWith(_nodeParts: TemplateNodePart[]): boolean {
        return false
    }

    canTransformPart(part: TemplateNodePart["part"], _context: INodeSceneInteractionsContext) {
        return isControlPointPart(part)
    }

    private dragGlobalUV(transform: Matrix4, part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): Vector2 | undefined {
        if (!isControlPointPart(part)) return undefined

        const translationWorld = transform.getTranslation()
        const translationCamera = context.camera.worldInverseMatrix.multiplyVectorXYZW(translationWorld.x, translationWorld.y, translationWorld.z, 1)
        const projection = context.camera.projectionMatrix.multiplyVectorXYZW(translationCamera[0], translationCamera[1], translationCamera[2], 1)

        const pointX = ((projection[0] / projection[3]) * 0.5 + 0.5) * context.canvasRect.width
        const pointY = ((-projection[1] / projection[3]) * 0.5 + 0.5) * context.canvasRect.height

        const mattress = this.getParentMattressNode()
        const baseMesh = mattress.getBaseMeshSceneNode(context)
        if (!baseMesh) return undefined

        const meshIntersection = context.getSceneNodePartsUnderCursor(pointX, pointY).find(({sceneNodePart}) => sceneNodePart.sceneNode.id === baseMesh.id)
        if (!meshIntersection || !meshIntersection.intersection.uv1) return undefined
        return Vector2.fromVector2Like(meshIntersection.intersection.uv1)
    }

    private controlPointsForGlobalUV(globalUV: Vector2, idx: number, context: INodeSceneInteractionsContext): MattressCurveParameters["controlPoints"] {
        const uv = this.fromGlobalUVSpace(globalUV, this.parameters.controlPoints[idx].uvSpace, context)
        const controlPoints = [...this.parameters.controlPoints]
        controlPoints[idx].uv = [uv.x, uv.y]
        return controlPoints
    }

    beginTransformInteraction(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext): TransformInteraction | undefined {
        if (!isControlPointPart(part)) return undefined
        const accessor = context.getLiveValueAccessor<MeshCurveUVControlPoint[]>(this)
        if (!accessor) return undefined
        const idx = getControlPointPartNumber(part)
        let lastControlPoints: MattressCurveParameters["controlPoints"] | undefined
        return {
            update: (transform) => {
                const globalUV = this.dragGlobalUV(transform, part, context)
                if (!globalUV) return
                lastControlPoints = this.controlPointsForGlobalUV(globalUV, idx, context)
                const localControlPoints = this.getLocalMeshCurveControlPoints(context)
                localControlPoints[idx] = {...localControlPoints[idx], uv: [globalUV.x, globalUV.y]}
                accessor.setLiveValue(localControlPoints)
            },
            commit: () => {
                accessor.endInteraction()
                if (lastControlPoints) this.updateParameters({controlPoints: lastControlPoints})
            },
            cancel: () => accessor.endInteraction(),
        }
    }

    getPartTransform(part: TemplateNodePart["part"], context: INodeSceneInteractionsContext) {
        if (!isControlPointPart(part)) throw new Error(`Expected part to be a control point, got ${part}`)

        const mattress = this.getParentMattressNode()
        const baseMeshSceneNode = mattress.getBaseMeshSceneNode(context)
        if (!baseMeshSceneNode) return Matrix4.identity()

        const {completeMeshData} = baseMeshSceneNode
        const {reified} = completeMeshData

        const controlPointIndex = getControlPointPartNumber(part)
        const controlPoint = this.parameters.controlPoints[controlPointIndex]

        const uv = new Vector2(controlPoint.uv[0], controlPoint.uv[1])
        const globalUV = this.toGlobalUVSpace(uv, controlPoint.uvSpace, context)
        const intersection = context.meshGeometryQueries.projectPointToMeshInUVSpace(
            reified as Parameters<INodeSceneInteractionsContext["meshGeometryQueries"]["projectPointToMeshInUVSpace"]>[0],
            MattresCurveUVChannel,
            Vector2.fromVector2Like(globalUV),
        )

        if (intersection === null) throw new Error("Failed to get point from UV")
        const controlPointPosition = intersection.position

        const mattressTransform = mattress.getTransform()
        const controlPointTransform = (() => {
            const position = mattressTransform.multiplyVectorXYZW(...controlPointPosition.toArray(), 1.0)
            const transform = new Matrix4()
            transform.setTranslation(new Vector3(position[0], position[1], position[2]))
            return transform
        })()
        return controlPointTransform
    }

    isDropTargetFor(_dropSourceNode: TemplateNode, _dropTargetPart: TemplateNodePart["part"]): boolean {
        return false
    }

    handleDroppedSource(_dropSourceNode: TemplateNode, _dropTargetPart: TemplateNodePart["part"], _context: INodeSceneInteractionsContext): void {}

    toGlobalUVSpace(uv: Vector2Like, uvSpace: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrameKind, context: IGetInfoNodeParts): Vector2Like {
        return uvSpace === "global" ? uv : uvSpace.toGlobalUVSpace(uv, context)
    }

    fromGlobalUVSpace(uv: Vector2Like, uvSpace: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrameKind, context: IGetInfoNodeParts): Vector2Like {
        return uvSpace === "global" ? uv : uvSpace.fromGlobalUVSpace(uv, context)
    }

    getLocalMeshCurveControlPoints(context: IGetInfoNodeParts): MeshCurveUVControlPoint[] {
        return this.parameters.controlPoints.map((cp) => {
            const globalUV = this.toGlobalUVSpace({x: cp.uv[0], y: cp.uv[1]}, cp.uvSpace, context)
            return {
                corner: cp.corner,
                sectionBreak: cp.sectionBreak,
                uv: [globalUV.x, globalUV.y],
            }
        })
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<MattressCurveData | null> {
        return scope.unresolvedToNull(evaluator.templateScope.resolve<MattressCurveData>(`mattressCurveData-${evaluator.getLocalId(this)}`))
    }
}

export type MattressCurveData = {
    localNode: MeshCurve
    path: MattressNodePath
    controlPoints: MattressCurveControlPointData[]
    closed: boolean
    resolution: number | undefined
}
